import { z } from 'zod';
import {
  getQuestionAnswer,
  getQuestionInputKind,
  normalizeCheckboxAnswer,
} from '../components/qc-assessment-modal/utils/qcAssessmentQuestionUtils';

/**
 * Zod shape for `answers` keyed by `String(question_id | index)`.
 * @param {object[]} sortedQuestions
 * @returns {import('zod').ZodObject}
 */
export function buildQcAssessmentAnswersSchema(sortedQuestions) {
  const shape = {};
  if (!Array.isArray(sortedQuestions)) {
    return z.object({});
  }

  sortedQuestions.forEach((q, idx) => {
    const qid =
      q?.question_id === undefined || q?.question_id === null
        ? idx
        : q.question_id;
    const idKey = String(qid);
    const kind = getQuestionInputKind(q);
    const required = Boolean(q?.property?.required);

    if (kind === 'yesOrNo') {
      shape[idKey] = required
        ? z.string().trim().min(1, 'Please select an option.')
        : z.string().optional().default('');
    } else if (kind === 'textAnswer') {
      shape[idKey] = required
        ? z.string().refine((s) => String(s ?? '').trim().length > 0, {
            message: 'This field is required.',
          })
        : z.string();
    } else if (kind === 'checkBox') {
      shape[idKey] = required
        ? z.preprocess(
            (v) => (Array.isArray(v) ? v : []),
            z.array(z.string()).min(1, 'Select at least one option.'),
          )
        : z.preprocess((v) => (Array.isArray(v) ? v : []), z.array(z.string()));
    } else {
      shape[idKey] = z.any().optional();
    }
  });

  return z.object(shape);
}

/**
 * Full QC assessment modal form (answers + optional transcript string for API).
 * @param {object[]} sortedQuestions
 * @returns {import('zod').ZodObject}
 */
export function buildQcAssessmentFormSchema(sortedQuestions) {
  return z.object({
    answers: buildQcAssessmentAnswersSchema(sortedQuestions),
    updatedTranscript: z.string().optional().default(''),
  });
}

/**
 * Default values for `useForm` / `reset` from saved `response_to_req` shape.
 * @param {object[]} sortedQuestions
 * @param {unknown} responseData
 * @param {string} [updatedTranscriptFallback]
 */
export function buildQcAssessmentDefaultValues(
  sortedQuestions,
  responseData,
  updatedTranscriptFallback = '',
) {
  const answers = {};
  if (!Array.isArray(sortedQuestions)) {
    return { answers: {}, updatedTranscript: updatedTranscriptFallback ?? '' };
  }

  sortedQuestions.forEach((q, idx) => {
    const qid =
      q?.question_id === undefined || q?.question_id === null
        ? idx
        : q.question_id;
    const idKey = String(qid);
    const kind = getQuestionInputKind(q);
    const raw = getQuestionAnswer(responseData, qid);

    if (kind === 'checkBox') {
      answers[idKey] = normalizeCheckboxAnswer(raw);
    } else if (kind === 'yesOrNo') {
      answers[idKey] = raw != null ? String(raw) : '';
    } else if (kind === 'textAnswer') {
      answers[idKey] = raw != null ? String(raw) : '';
    } else {
      answers[idKey] = '';
    }
  });

  return {
    answers,
    updatedTranscript:
      typeof updatedTranscriptFallback === 'string'
        ? updatedTranscriptFallback
        : '',
  };
}
